import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useMutation } from 'react-query'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'

import InlineLoader from '@/components/InlineLoader'
import Loader from '@/components/Loading'
import Page from '@/components/Page'
import useMessage from '@/hooks/useMessage'
import {
  dataSourceAuthorizeQueryOptions,
  dataSourceConnectQueryOptions
} from '@/pages/SpaceAppBuilder/utilities/queries'
import { useAppSelector } from '@/store/hooks'
import { getConfiguration } from '@/store/selectors'

import messages from './messages'

const Container = styled.section`
  margin: 25px auto;
  max-width: 900px;
  display: flex;
  height: 100vh;
  justify-content: center;
  align-items: center;
`

const StyledTitle = styled.h1`
  font-size: 1.25rem;
  color: gray;
  font-weight: 500;
  text-align: center;
`

const LoadingContainer = styled.div`
  display: grid;
  align-items: center;
  justify-items: center;
  margin-top: 15px;
`

const AuthorizeLandingPage = () => {
  const intl = useIntl()

  const { sendToParent } = useMessage('workgrid-auth', () => {})
  const [isConnected, setIsConnected] = useState(false)

  const location = useLocation()
  const tenantConfig = useAppSelector(getConfiguration)

  const createSessionSearch = new URLSearchParams(decodeURIComponent(location.search))
  const spaceId: string = createSessionSearch.get('spaceId') || ''
  const datasourceId: string = createSessionSearch.get('datasourceId') || ''
  const datasourceInstanceId: string = createSessionSearch.get('datasourceInstanceId') || ''
  const name: string = createSessionSearch.get('name') || ''
  const applicationId: string = createSessionSearch.get('applicationId') || ''
  const configuration: string = createSessionSearch.get('configuration') || ''

  const hash = new URLSearchParams(location.hash)
  const token: string = hash.get('#token') || ''

  const query = new URLSearchParams(location.search)
  const state: string = query.get('state') || ''
  const stateParams = new URLSearchParams(decodeURIComponent(state))

  useMessage('workgrid-auth', (send, payload) => {
    if (payload.success) {
      setIsConnected(true)
      sendToParent({ type: 'workgrid-auth', payload: { success: true } })
    } else {
      sendToParent({ type: 'workgrid-auth', payload: { success: false, error: payload.error } })
    }
  })

  const createSession = useMutation({
    mutationFn: async (payload: any) => {
      const url = `${tenantConfig?.apiHost}${dataSourceAuthorizeQueryOptions({ spaceId, datasourceId }).queryKey[0]}`
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'x-workgrid-space': `${spaceId}`,
          'content-type': 'application/json'
        },
        body: JSON.stringify(payload)
      })
      if (!response.ok) {
        if (response.headers.get('Content-Type')?.startsWith('application/json')) {
          const json = await response.json()
          throw { ...json, status: response.status }
        } else {
          const text = await response.text()
          throw { text, status: response.status }
        }
      }

      if (response.headers.get('Content-Type')?.startsWith('application/json')) {
        return response.json()
      }
    }
  })

  const updateDataSource = useMutation({
    mutationFn: async (payload: any) => {
      const url = `${tenantConfig?.apiHost}${dataSourceConnectQueryOptions().queryKey[0]}`
      const sessionId: string = stateParams.get('sessionId') || ''
      const token: string = sessionStorage.getItem(sessionId) || ''
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'x-workgrid-space': `${spaceId}`,
          'content-type': 'application/json'
        },
        body: JSON.stringify(payload)
      })
      if (!response.ok) {
        if (response.headers.get('Content-Type')?.startsWith('application/json')) {
          const json = await response.json()
          throw { ...json, status: response.status }
        } else {
          const text = await response.text()
          throw { text, status: response.status }
        }
      }

      if (response.headers.get('Content-Type')?.startsWith('application/json')) {
        return response.json()
      }
      setIsConnected(true)
    }
  })

  const createSessionAndConnect = async () => {
    const payload = {
      ...(configuration && { configuration: configuration ? JSON.parse(decodeURIComponent(configuration)) : {} }),
      ...(name && { name: decodeURIComponent(name) }),
      ...(applicationId && { applicationId: applicationId }),
      ...(datasourceInstanceId && { datasourceInstanceId: datasourceInstanceId })
    }

    await createSession
      .mutateAsync(payload)
      .then(session => {
        const query = new URLSearchParams(session.oauthUrl)
        const state: string = query.get('state') || ''
        const stateParams = new URLSearchParams(decodeURIComponent(state))
        const sessionId: string = stateParams.get('sessionId') || ''
        sessionStorage.setItem(sessionId, token)

        const authWindow = window.open(session.oauthUrl)
        authWindow?.focus()
      })
      .catch(error => {
        sendToParent({ type: 'workgrid-auth', payload: { success: false, error: error } })
      })
  }

  const connectDatasource = async () => {
    const payload = {
      sessionId: stateParams.get('sessionId'),
      code: query.get('code')
    }

    try {
      await updateDataSource.mutateAsync(payload)
      sendToParent({ type: 'workgrid-auth', payload: { success: true } })
    } catch (error) {
      sendToParent({ type: 'workgrid-auth', payload: { success: false, error: error } })
    } finally {
      const sessionId: string = stateParams.get('sessionId') || ''
      sessionStorage.removeItem(sessionId)
    }
  }

  useEffect(() => {
    if (stateParams.get('datasourceBuilder')) {
      sendToParent({ type: 'workgrid-auth', payload: { success: true, code: query.get('code') } })
    } else if (tenantConfig?.apiHost && spaceId && datasourceId && (datasourceInstanceId || (configuration && name))) {
      createSessionAndConnect()
    } else if (tenantConfig?.apiHost && stateParams.has('sessionId')) {
      connectDatasource()
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [tenantConfig])

  const dataSourceName = stateParams.get('name')

  return (
    <Page>
      <Container>
        <InlineLoader
          useQueryResult={createSession}
          errorOptions={{ message: intl.formatMessage(messages.authorizeError) }}
          loaderComponent={<Loader isLoading={true} loadingMessage={intl.formatMessage(messages.authorizeLoading)} />}
        >
          {() => (
            <StyledTitle>
              {isConnected
                ? ` You may now close this window and return to the application.`
                : `Setting up your ${dataSourceName || `data source`} connection, hold tight while we set things up for you.`}
            </StyledTitle>
          )}
        </InlineLoader>
        <InlineLoader
          useQueryResult={updateDataSource}
          errorOptions={{ message: intl.formatMessage(messages.connectionError) }}
          loaderComponent={
            <LoadingContainer>
              <Loader isLoading={true} loadingMessage={intl.formatMessage(messages.connectionLoading)} />
            </LoadingContainer>
          }
        >
          {() => (
            <StyledTitle>
              {`Your ${
                dataSourceName || `data source`
              } connection was successfully created. You may now close this window and return to
                      the application.`}
            </StyledTitle>
          )}
        </InlineLoader>
      </Container>
    </Page>
  )
}

AuthorizeLandingPage.displayName = 'AuthorizeLandingPage'

export default AuthorizeLandingPage
